import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import BedIcon from '@mui/icons-material/Bed';
import { Alert, Box, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { isLTLEHotelOfferType } from '~/utils/offer';

import RoomAssetsTab from './RoomAssetsTab';
import { getUniqueRoomAssets } from './utils';

const ROOM_ASSETS_TAB = 'room-assets';

export default function OfferBuilderAssetsPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const hasOfferLoadError = !isLoadingOffer && !!offerError && !offer;
  const {
    data: opportunityRelated,
    isLoading: isLoadingRelated,
    error: relatedError,
  } = useGetOpportunityRelated(offerId, { enabled: isSupportedOfferType });
  const roomAssets = useMemo(() => getUniqueRoomAssets(opportunityRelated?.deals), [opportunityRelated?.deals]);
  const hotelName = offer?.name || 'Property';

  return (
    <>
      <Helmet>
        <title>Assets | {hotelName}</title>
      </Helmet>

      {hasOfferLoadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load offer details.
        </Alert>
      )}

      {!isLoadingOffer && offer && !isSupportedOfferType && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Offer Builder is only available for flash offers.
        </Alert>
      )}

      {!isLoadingOffer && isSupportedOfferType && (
        <>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Assets
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Review sellable room assets synced from Salesforce.
          </Typography>

          <Tabs value={ROOM_ASSETS_TAB} sx={{ mb: 1 }}>
            <Tab
              value={ROOM_ASSETS_TAB}
              icon={<BedIcon fontSize="small" />}
              iconPosition="start"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>Room Assets</span>
                  <Chip label={roomAssets.length} size="small" />
                </Stack>
              }
            />
          </Tabs>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select a room asset to inspect synced room, bedding, occupancy, rack rate, and system information.
            </Typography>
          </Box>

          {relatedError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              Unable to load synced room assets.
            </Alert>
          ) : (
            <RoomAssetsTab deals={opportunityRelated?.deals} isLoading={isLoadingRelated} />
          )}
        </>
      )}
    </>
  );
}
